import React, { useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router';
import { useToggle } from 'react-use';
import ExportButton from './ExportButton';
import { Button, Box } from '../design-system';
import { PROJECT_TYPES } from '../containers/Projects/constants';
import Alert from '../helpers/alert';
import {
  catchPlanDetailError,
  checkPermissions,
  createSelectOptions,
  dateValidate,
  required,
} from '../helpers/utils';
import ModalForm from './ModalForm';
import { Session } from '../contexts/sessionContext';
import { PERMISSIONS } from '../globalConstants';

interface AddProjectModalProps {
  initialValues?: any;
  clientData: any[];
  funderData: any[];
}

const AddProjectModal = ({
  initialValues,
  clientData,
  funderData,
}: AddProjectModalProps) => {
  const {
    state: { user },
  } = useContext(Session);

  const canCreate = checkPermissions(user.permissions, [
    PERMISSIONS.CREATE_PROJECTS,
  ]);

  const navigate = useNavigate();
  const [isOpen, toggleOpen] = useToggle(false);
  const clientOptions = createSelectOptions(clientData);
  const funderOptions = createSelectOptions(funderData);
  const memberOptions = createSelectOptions(user.members);

  const onSubmit = async (values) => {
    try {
      const {
        data: { id },
      } = await axios.post('/project', {
        ...values,
        hard_deadline: values.hard_deadline && 1,
      });
      navigate(`/projects/edit/${id}`);
      Alert.success('Project successfully created');
    } catch (error) {
      catchPlanDetailError(error?.response);
    }
  };

  const lackPermission = () => {
    Alert.error(
      'You are an external user and do not have permission to export data. Please contact the software administrator to generate this report for you.',
    );
  };

  return (
    <>
      {canCreate && (
        <Button.Group>
          <Button type="button" onClick={toggleOpen}>
            New Project
          </Button>
          {user.isClient && (
            <Button variant="green" onClick={lackPermission}>
              Export
            </Button>
          )}
          {!user.isClient && (
            <Box ml="1rem">
              <ExportButton type="projects" />
            </Box>
          )}
        </Button.Group>
      )}
      <ModalForm
        title="Create New Project"
        isOpen={isOpen}
        onSubmit={onSubmit}
        onClose={toggleOpen}
        initialValues={initialValues}
        fields={[
          {
            name: 'description',
            label: 'Project Description',
            validate: required,
          },
          {
            name: 'client',
            label: 'Client',
            component: 'select',
            options: clientOptions,
            validate: required,
          },
          {
            name: 'funder',
            label: 'Funder',
            component: 'select',
            options: funderOptions,
          },
          {
            name: 'project_lead',
            label: 'Project Lead',
            component: 'select',
            options: memberOptions,
            validate: required,
          },
          {
            name: 'type',
            label: 'Project Type',
            component: 'select',
            options: PROJECT_TYPES,
            validate: required,
          },
          {
            name: 'deadline',
            label: 'Deadline',
            type: 'Date',
            validate: dateValidate,
          },
          {
            id: 'projectCreateHardDeadline',
            name: 'hard_deadline',
            label: 'Hard Deadline',
            component: 'checkbox',
            width: 150,
          },
        ]}
        oneColumn
      />
    </>
  );
};

export default AddProjectModal;
